import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";

import { i18n } from "../../translate/i18n";
import { head } from "lodash";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";
import ChatiaDialog from "../ChatiaDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
    },
  },
  select: {
    borderRadius: "12px !important",
    "& .MuiSelect-outlined": {
      borderRadius: "12px !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "12px !important",
    },
  },
  selectMenu: {
    borderRadius: 14,
    boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
    border: "none",
    background: theme.palette.type === "dark" ? "#121212" : "#ffffff",
    backgroundImage: "none",
    marginTop: 8,
    overflowX: "hidden",
    overflowY: "auto",
    ...theme.scrollbarStyles,
    "& .MuiList-root": {
      padding: 6,
    },
  },
  selectMenuItem: {
    borderRadius: 8,
    padding: "9px 12px",
    marginBottom: 4,
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#ffffff" : "#1a1a1a",
    "&:hover, &.Mui-focusVisible": {
      background: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
    },
    "&.Mui-selected, &.Mui-selected:hover": {
      background: "rgba(6, 207, 156, 0.10)",
    },
  },
  attachmentBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    border: `1px dashed ${theme.palette.type === "dark" ? "rgba(255,255,255,0.16)" : "rgba(15,23,42,0.18)"}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
  },
}));

const AnnouncementSchema = Yup.object().shape({
  title: Yup.string().required(i18n.t("validation.required")),
  text: Yup.string().required(i18n.t("validation.required")),
});

const AnnouncementModal = ({ open, onClose, announcementId, reload, resetPagination }) => {
  const classes = useStyles();

  const selectMenuProps = {
    PaperProps: {
      className: classes.selectMenu,
    },
    getContentAnchorEl: null,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  };

  const initialState = {
    title: "",
    text: "",
    priority: 3,
    status: true,
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [announcement, setAnnouncement] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);

  useEffect(() => {
    try {
      (async () => {
        if (!announcementId) return;

        const { data } = await api.get(`/announcements/${announcementId}`);
        setAnnouncement((prevState) => {
          return { ...prevState, ...data };
        });
      })();
    } catch (err) {
      toastError(err);
    }
  }, [announcementId, open]);

  const handleClose = () => {
    setAnnouncement(initialState);
    setAttachment(null);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleReload = () => {
    if (typeof reload === "function") {
      reload();
    }

    if (typeof resetPagination === "function") {
      resetPagination();
    }
  };

  const handleSaveAnnouncement = async (values) => {
    const announcementData = { ...values };
    try {
      if (announcementId) {
        await api.put(`/announcements/${announcementId}`, announcementData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "announcements");
          formData.append("file", attachment);
          await api.post(
            `/announcements/${announcementId}/media-upload`,
            formData
          );
        }
      } else {
        const { data } = await api.post("/announcements", announcementData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "announcements");
          formData.append("file", attachment);

          await api.post(`/announcements/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("announcements.toasts.success"));
      handleReload();
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (announcement.mediaPath) {
      await api.delete(`/announcements/${announcement.id}/media-upload`);
      setAnnouncement((prev) => ({
        ...prev,
        mediaPath: null,
      }));
      toast.success(i18n.t("announcements.toasts.deleted"));
      handleReload();
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("announcements.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("announcements.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <div style={{ display: "none" }}>
        <input
          type="file"
          accept=".png,.jpg,.jpeg"
          ref={attachmentFile}
          onChange={(e) => handleAttachmentFile(e)}
        />
      </div>
      <Formik
        initialValues={announcement}
        enableReinitialize={true}
        validationSchema={AnnouncementSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveAnnouncement(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ touched, errors, isSubmitting, submitForm, values, setFieldValue }) => (
          <ChatiaDialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            title={announcementId
              ? `${i18n.t("announcements.dialog.edit")}`
              : `${i18n.t("announcements.dialog.add")}`}
            onSave={submitForm}
            saveType="button"
            saveDisabled={isSubmitting}
            saveLoading={isSubmitting}
            saveText={announcementId
              ? `${i18n.t("announcements.dialog.buttons.edit")}`
              : `${i18n.t("announcements.dialog.buttons.add")}`}
            footerChildren={
              <>
                {!attachment && !announcement.mediaPath && (
                  <Button
                    color="primary"
                    onClick={() => attachmentFile.current.click()}
                    disabled={isSubmitting}
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                    style={{
                      borderRadius: 12,
                      height: 38,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {i18n.t("announcements.dialog.buttons.attach")}
                  </Button>
                )}
              </>
            }
          >
            <Form>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("announcements.dialog.subtitle")}
                </Typography>
              </Box>
                <Grid spacing={2} container>
                  <Grid xs={12} item>
                    <Field
                      as={TextField}
                      className={classes.field}
                      label={i18n.t("announcements.dialog.form.title")}
                      name="title"
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <Field
                      as={TextField}
                      className={classes.field}
                      label={i18n.t("announcements.dialog.form.text")}
                      name="text"
                      error={touched.text && Boolean(errors.text)}
                      helperText={touched.text && errors.text}
                      variant="outlined"
                      margin="dense"
                      multiline={true}
                      minRows={7}
                      fullWidth
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <InputLabel>{i18n.t("announcements.dialog.form.status")}</InputLabel>
                      <Select
                        className={classes.select}
                        value={values.status}
                        onChange={(e) => setFieldValue("status", e.target.value)}
                        label={i18n.t("announcements.dialog.form.status")}
                        MenuProps={selectMenuProps}
                      >
                        <MenuItem value={true} className={classes.selectMenuItem}>{i18n.t("announcements.dialog.form.active")}</MenuItem>
                        <MenuItem value={false} className={classes.selectMenuItem}>{i18n.t("announcements.dialog.form.inactive")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} item>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <InputLabel>{i18n.t("announcements.dialog.form.priority")}</InputLabel>
                      <Select
                        className={classes.select}
                        value={values.priority}
                        onChange={(e) => setFieldValue("priority", e.target.value)}
                        label={i18n.t("announcements.dialog.form.priority")}
                        MenuProps={selectMenuProps}
                      >
                        <MenuItem value={1} className={classes.selectMenuItem}>{i18n.t("announcements.dialog.form.high")}</MenuItem>
                        <MenuItem value={2} className={classes.selectMenuItem}>{i18n.t("announcements.dialog.form.medium")}</MenuItem>
                        <MenuItem value={3} className={classes.selectMenuItem}>{i18n.t("announcements.dialog.form.low")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {(announcement.mediaPath || attachment) && (
                    <Grid xs={12} item>
                      <div className={classes.attachmentBox}>
                        <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0 }}>
                          <AttachFileIcon color="primary" fontSize="small" />
                          <Typography variant="body2" noWrap>
                            {attachment ? attachment.name : announcement.mediaName}
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => setConfirmationOpen(true)}
                          color="secondary"
                          size="small"
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </div>
                    </Grid>
                  )}
                </Grid>
            </Form>
          </ChatiaDialog>
        )}
      </Formik>
    </div>
  );
};

export default AnnouncementModal;
